import {drawRectangle} from '../../canvas-timeline/canvas_timeline_util';

/** Mirrors TaskSetChip's 19px pill so the two surfaces read the same. */
const PILL_HEIGHT = 15;
const PILL_RADIUS = 7.5;
const ICON_SIZE = 9;
const PILL_PADDING_X = 5;
const ICON_LABEL_GAP = 3;
const EDGE_WIDTH = 4;
// Padding added around the painted pill for hit-testing only -- see drawTaskSetChip.
const CHIP_HIT_PADDING = 4;
const CHIP_FONT = '600 10px sans-serif';

export type ChipTier = 'full' | 'icon' | 'edge' | 'none';

export interface ChipRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

const iconOnlyWidth = ICON_SIZE + PILL_PADDING_X * 2;

/**
 * Which tier fits, and how wide it is.
 *
 * Measured in bar pixels, never in zoom levels: at one zoom a one-day task and a
 * three-week task have wildly different widths, so the bar is the only honest
 * unit. This is the same arbitration drawTaskNameAndIcons already does with
 * measureText.
 */
export const measureChip = ({
	canvasContext,
	availableWidth,
	edgeWidthAvailable,
	label,
}: {
	canvasContext: CanvasRenderingContext2D;
	/**
	 * Room left for the PILL after the task name's own minimum and the right-hand
	 * icons have been reserved. May be negative on a narrow bar, which simply means
	 * no pill fits.
	 */
	availableWidth: number;
	/**
	 * The bar's own width. The edge tier lives in the bar's padding and costs the
	 * name nothing, so it is measured against this rather than against the pill
	 * budget -- which is what lets the marker survive on bars too narrow to carry a
	 * name at all, the case AC10 is really about.
	 */
	edgeWidthAvailable: number;
	label: string | null;
}): {tier: ChipTier; width: number} => {
	if (label) {
		const previousFont = canvasContext.font;
		canvasContext.font = CHIP_FONT;
		const labelWidth = canvasContext.measureText(label).width;
		canvasContext.font = previousFont;

		const fullWidth = iconOnlyWidth + ICON_LABEL_GAP + labelWidth;
		if (availableWidth >= fullWidth) {
			return {tier: 'full', width: fullWidth};
		}
	}

	if (availableWidth >= iconOnlyWidth) {
		return {tier: 'icon', width: iconOnlyWidth};
	}

	if (edgeWidthAvailable >= EDGE_WIDTH) {
		return {tier: 'edge', width: EDGE_WIDTH};
	}

	return {tier: 'none', width: 0};
};

/**
 * Paints the chip and returns the rect to hit-test, or null where there is
 * nothing clickable.
 *
 * The edge tier is deliberately not clickable: 4px beside a bar whose whole body
 * opens the task modal would generate misclicks. At that width the chip is doing
 * its AC10 job -- marking the series -- and not its AC8 job.
 */
export const drawTaskSetChip = ({
	canvasContext,
	x,
	y,
	barHeight,
	tier,
	width,
	label,
	colour,
}: {
	canvasContext: CanvasRenderingContext2D;
	x: number;
	y: number;
	barHeight: number;
	tier: ChipTier;
	width: number;
	label: string | null;
	colour: {background: string; foreground: string};
}): ChipRect | null => {
	if (tier === 'none') {
		return null;
	}

	if (tier === 'edge') {
		canvasContext.fillStyle = colour.foreground;
		canvasContext.fillRect(x, y, EDGE_WIDTH, barHeight);
		return null;
	}

	const pillY = y + (barHeight - PILL_HEIGHT) / 2;

	drawRectangle(canvasContext, x, pillY, width, PILL_HEIGHT, {
		backgroundColor: colour.background,
		borderRadius: PILL_RADIUS,
	});

	// The repeat glyph, stroked rather than an image so it takes the set's colour.
	const iconX = x + PILL_PADDING_X;
	const iconY = pillY + (PILL_HEIGHT - ICON_SIZE) / 2;
	const r = ICON_SIZE / 2;
	canvasContext.save();
	canvasContext.strokeStyle = colour.foreground;
	canvasContext.lineWidth = 1.2;
	canvasContext.lineCap = 'round';
	canvasContext.beginPath();
	canvasContext.arc(iconX + r, iconY + r, r, 0.6, 5.0);
	canvasContext.stroke();
	canvasContext.restore();

	if (tier === 'full' && label) {
		canvasContext.save();
		canvasContext.font = CHIP_FONT;
		canvasContext.fillStyle = colour.foreground;
		canvasContext.textBaseline = 'middle';
		canvasContext.fillText(label, iconX + ICON_SIZE + ICON_LABEL_GAP, pillY + PILL_HEIGHT / 2);
		canvasContext.restore();
	}

	// PIKA-748 F3: the hit rect is padded beyond the painted pill.
	//
	// Functional review reported that a chip click "only works at the x of y tier".
	// Both pill tiers are clickable by construction -- this function returns a rect
	// for each, and task_item sets taskSetChipClickable from the set index, not from
	// the tier -- so the gate was never the difference. What differs is size: the
	// icon-only pill is 19x15, against 52+ for the full pill. A 19px target beside a
	// bar whose whole body opens the task modal is easy to miss by a pixel or two,
	// and a miss looks exactly like "not clickable".
	//
	// Clamped to the bar vertically, so the pad can never make the chip swallow
	// clicks meant for the row above or below.
	const hitTop = Math.max(y, pillY - CHIP_HIT_PADDING);
	const hitBottom = Math.min(y + barHeight, pillY + PILL_HEIGHT + CHIP_HIT_PADDING);

	return {
		x: x - CHIP_HIT_PADDING,
		y: hitTop,
		width: width + CHIP_HIT_PADDING * 2,
		height: hitBottom - hitTop,
	};
};
